"use client";
import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { updateUser, uploadAvatar, getData, getAuthHeaders, deleteUserAccount } from '../funcs/api';
import { toast } from 'react-toastify';
import Link from 'next/link';
import MyShortLinks from '../components/my-short-links/my-short-links';
// import DeleteAccountCard from '@/app/components/DeleteAccountCard';
import DeleteAccountCard from '../components/DeleteAccountCard';
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { loginUserGoogleAllInOne } from "@/app/funcs/auth";

function AddToAppleWalletButton({ user: userProp }) {
    const [loading, setLoading] = useState(false);

    const getUser = () => {
        if (userProp && (userProp.id || userProp.slug || userProp.name)) return userProp;
        if (typeof window === 'undefined') return null;
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    };

    const handleAddToWallet = async () => {
        setLoading(true);
        try {
            const headers = getAuthHeaders();
            if (!headers.Authorization) {
                toast.error('يجب تسجيل الدخول أولاً');
                return;
            }
            const user = getUser();
            if (!user) {
                toast.error('لم يتم العثور على بيانات المستخدم');
                return;
            }
            const body = {
                id: user.id ?? user.ID,
                slug: user.slug,
                name: user.name ?? user.display_name,
            };
            const res = await fetch('/api/account/wallet-pass', {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                toast.error(err.error || err.message || 'فشل جلب البطاقة');
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'figmawya.pkpass';
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('تم تحميل البطاقة. على iPhone افتح الملف لإضافتها إلى المحفظة.');
        } catch (e) {
            toast.error('حدث خطأ: ' + (e.message || 'جرب لاحقاً'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleAddToWallet}
            disabled={loading}
            className="btn btn-dark d-inline-flex align-items-center gap-2"
            aria-label="Add to Apple Wallet"
        >
            {loading ? (
                <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    جاري التحميل...
                </>
            ) : (
                <>
                    <i className="bi bi-wallet2"></i>
                    <span>Add to Apple Wallet</span>
                </>
            )}
        </button>
    );
}

function AddToGoogleWalletButton({ user: userProp }) {
    const [loading, setLoading] = useState(false);

    const getUser = () => {
        if (userProp && (userProp.id || userProp.slug || userProp.name)) return userProp;
        if (typeof window === 'undefined') return null;
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    };

    const handleAddToGoogleWallet = async () => {
        setLoading(true);
        try {
            const headers = getAuthHeaders();
            if (!headers.Authorization) {
                toast.error('يجب تسجيل الدخول أولاً');
                return;
            }
            const user = getUser();
            if (!user) {
                toast.error('لم يتم العثور على بيانات المستخدم');
                return;
            }
            const body = {
                id: user.id ?? user.ID,
                slug: user.slug,
                name: user.name ?? user.display_name,
            };
            const res = await fetch('/api/account/google-wallet-pass', {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                const msg = err.error || err.message || 'فشل جلب رابط Google Wallet';
                if (res.status === 503) {
                    toast.error('Google Wallet غير مُهيّأ. أضف GOOGLE_WALLET_ISSUER_ID و GOOGLE_WALLET_SERVICE_ACCOUNT_JSON في .env.local');
                } else {
                    toast.error(msg);
                }
                return;
            }
            const data = await res.json();
            if (data.url) {
                window.open(data.url, '_blank', 'noopener,noreferrer');
                toast.success('تم فتح رابط إضافة البطاقة إلى Google Wallet.');
            } else {
                toast.error('لم يتم استلام الرابط');
            }
        } catch (e) {
            toast.error('حدث خطأ: ' + (e.message || 'جرب لاحقاً'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleAddToGoogleWallet}
            disabled={loading}
            className="btn btn-outline-dark d-inline-flex align-items-center gap-2"
            aria-label="Add to Google Wallet"
        >
            {loading ? (
                <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    جاري التحميل...
                </>
            ) : (
                <>
                    <i className="bi bi-phone"></i>
                    <span>Add to Google Wallet</span>
                </>
            )}
        </button>
    );
}

export default function UserSettings(props) {

    const router = useRouter();
    const [user, setUser] = useState(props.user);
    const searchParams = useSearchParams();
    const [activeSection, setActiveSection] = useState(props.activeSection ? props.activeSection : 'default');
    const [avatar, setAvatar] = useState(props.avatar);
    const [form, setForm] = useState({ name: '', description: '', email: '', password: '' });
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [userContent, setUserContent] = useState({
        articles: [],
        plugins: [],
        templates: [],
        challenges: [],
        podcasts: [],
        certificates: [],
        favorites: [],
        workSamples: [],
        jobs: []
    });
    const [loading, setLoading] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [isCommAdminUser, setIsCommAdminUser] = useState(false);

    // Get the current section from URL search params
    useEffect(() => {
        const section = searchParams.get('section') || 'default';
        setActiveSection(section);
    }, [searchParams]);

    // Comprehensive country data with English names, Arabic names, and flags
    const countries = [
        { en: 'Egypt', ar: 'مصر', flag: '🇪🇬' },
        { en: 'Saudi Arabia', ar: 'السعودية', flag: '🇸🇦' },
        { en: 'Kuwait', ar: 'الكويت', flag: '🇰🇼' },
        { en: 'UAE', ar: 'الإمارات', flag: '🇦🇪' },
        { en: 'Qatar', ar: 'قطر', flag: '🇶🇦' },
        { en: 'Bahrain', ar: 'البحرين', flag: '🇧🇭' },
        { en: 'Oman', ar: 'عمان', flag: '🇴🇲' },
        { en: 'Jordan', ar: 'الأردن', flag: '🇯🇴' },
        { en: 'Lebanon', ar: 'لبنان', flag: '🇱🇧' },
        { en: 'Syria', ar: 'سوريا', flag: '🇸🇾' },
        { en: 'Iraq', ar: 'العراق', flag: '🇮🇶' },
        { en: 'Yemen', ar: 'اليمن', flag: '🇾🇪' },
        { en: 'Palestine', ar: 'فلسطين', flag: '🇵🇸' },
        { en: 'Morocco', ar: 'المغرب', flag: '🇲🇦' },
        { en: 'Algeria', ar: 'الجزائر', flag: '🇩🇿' },
        { en: 'Tunisia', ar: 'تونس', flag: '🇹🇳' },
        { en: 'Libya', ar: 'ليبيا', flag: '🇱🇾' },
        { en: 'Sudan', ar: 'السودان', flag: '🇸🇩' },
        { en: 'Somalia', ar: 'الصومال', flag: '🇸🇴' },
        { en: 'Djibouti', ar: 'جيبوتي', flag: '🇩🇯' },
        { en: 'Comoros', ar: 'جزر القمر', flag: '🇰🇲' },
        { en: 'Mauritania', ar: 'موريتانيا', flag: '🇲🇷' },
        { en: 'USA', ar: 'الولايات المتحدة', flag: '🇺🇸' },
        { en: 'United States', ar: 'الولايات المتحدة', flag: '🇺🇸' },
        { en: 'UK', ar: 'المملكة المتحدة', flag: '🇬🇧' },
        { en: 'United Kingdom', ar: 'المملكة المتحدة', flag: '🇬🇧' },
        { en: 'Canada', ar: 'كندا', flag: '🇨🇦' },
        { en: 'Australia', ar: 'أستراليا', flag: '🇦🇺' },
        { en: 'Germany', ar: 'ألمانيا', flag: '🇩🇪' },
        { en: 'France', ar: 'فرنسا', flag: '🇫🇷' },
        { en: 'Italy', ar: 'إيطاليا', flag: '🇮🇹' },
        { en: 'Spain', ar: 'إسبانيا', flag: '🇪🇸' },
        { en: 'Netherlands', ar: 'هولندا', flag: '🇳🇱' },
        { en: 'Belgium', ar: 'بلجيكا', flag: '🇧🇪' },
        { en: 'Switzerland', ar: 'سويسرا', flag: '🇨🇭' },
        { en: 'Austria', ar: 'النمسا', flag: '🇦🇹' },
        { en: 'Sweden', ar: 'السويد', flag: '🇸🇪' },
        { en: 'Norway', ar: 'النرويج', flag: '🇳🇴' },
        { en: 'Denmark', ar: 'الدنمارك', flag: '🇩🇰' },
        { en: 'Finland', ar: 'فنلندا', flag: '🇫🇮' },
        { en: 'Poland', ar: 'بولندا', flag: '🇵🇱' },
        { en: 'Czech Republic', ar: 'جمهورية التشيك', flag: '🇨🇿' },
        { en: 'Hungary', ar: 'المجر', flag: '🇭🇺' },
        { en: 'Romania', ar: 'رومانيا', flag: '🇷🇴' },
        { en: 'Bulgaria', ar: 'بلغاريا', flag: '🇧🇬' },
        { en: 'Greece', ar: 'اليونان', flag: '🇬🇷' },
        { en: 'Turkey', ar: 'تركيا', flag: '🇹🇷' },
        { en: 'Russia', ar: 'روسيا', flag: '🇷🇺' },
        { en: 'Ukraine', ar: 'أوكرانيا', flag: '🇺🇦' },
        { en: 'Belarus', ar: 'بيلاروسيا', flag: '🇧🇾' },
        { en: 'Moldova', ar: 'مولدوفا', flag: '🇲🇩' },
        { en: 'Latvia', ar: 'لاتفيا', flag: '🇱🇻' },
        { en: 'Lithuania', ar: 'ليتوانيا', flag: '🇱🇹' },
        { en: 'Estonia', ar: 'إستونيا', flag: '🇪🇪' },
        { en: 'Slovakia', ar: 'سلوفاكيا', flag: '🇸🇰' },
        { en: 'Slovenia', ar: 'سلوفينيا', flag: '🇸🇮' },
        { en: 'Croatia', ar: 'كرواتيا', flag: '🇭🇷' },
        { en: 'Serbia', ar: 'صربيا', flag: '🇷🇸' },
        { en: 'Bosnia', ar: 'البوسنة والهرسك', flag: '🇧🇦' },
        { en: 'Montenegro', ar: 'الجبل الأسود', flag: '🇲🇪' },
        { en: 'Macedonia', ar: 'مقدونيا', flag: '🇲🇰' },
        { en: 'Albania', ar: 'ألبانيا', flag: '🇦🇱' },
        { en: 'Kosovo', ar: 'كوسوفو', flag: '🇽🇰' },
        { en: 'Malta', ar: 'مالطا', flag: '🇲🇹' },
        { en: 'Cyprus', ar: 'قبرص', flag: '🇨🇾' },
        { en: 'Iceland', ar: 'آيسلندا', flag: '🇮🇸' },
        { en: 'Ireland', ar: 'أيرلندا', flag: '🇮🇪' },
        { en: 'Portugal', ar: 'البرتغال', flag: '🇵🇹' },
        { en: 'Luxembourg', ar: 'لوكسمبورغ', flag: '🇱🇺' },
        { en: 'Monaco', ar: 'موناكو', flag: '🇲🇨' },
        { en: 'Liechtenstein', ar: 'ليختنشتاين', flag: '🇱🇮' },
        { en: 'Andorra', ar: 'أندورا', flag: '🇦🇩' },
        { en: 'San Marino', ar: 'سان مارينو', flag: '🇸🇲' },
        { en: 'Vatican', ar: 'الفاتيكان', flag: '🇻🇦' },
        { en: 'Japan', ar: 'اليابان', flag: '🇯🇵' },
        { en: 'China', ar: 'الصين', flag: '🇨🇳' },
        { en: 'South Korea', ar: 'كوريا الجنوبية', flag: '🇰🇷' },
        { en: 'North Korea', ar: 'كوريا الشمالية', flag: '🇰🇵' },
        { en: 'Mongolia', ar: 'منغوليا', flag: '🇲🇳' },
        { en: 'Taiwan', ar: 'تايوان', flag: '🇹🇼' },
        { en: 'Hong Kong', ar: 'هونغ كونغ', flag: '🇭🇰' },
        { en: 'Macau', ar: 'ماكاو', flag: '🇲🇴' },
        { en: 'Vietnam', ar: 'فيتنام', flag: '🇻🇳' },
        { en: 'Laos', ar: 'لاوس', flag: '🇱🇦' },
        { en: 'Cambodia', ar: 'كمبوديا', flag: '🇰🇭' },
        { en: 'Thailand', ar: 'تايلاند', flag: '🇹🇭' },
        { en: 'Myanmar', ar: 'ميانمار', flag: '🇲🇲' },
        { en: 'Malaysia', ar: 'ماليزيا', flag: '🇲🇾' },
        { en: 'Singapore', ar: 'سنغافورة', flag: '🇸🇬' },
        { en: 'Indonesia', ar: 'إندونيسيا', flag: '🇮🇩' },
        { en: 'Philippines', ar: 'الفلبين', flag: '🇵🇭' },
        { en: 'Brunei', ar: 'بروناي', flag: '🇧🇳' },
        { en: 'East Timor', ar: 'تيمور الشرقية', flag: '🇹🇱' },
        { en: 'India', ar: 'الهند', flag: '🇮🇳' },
        { en: 'Pakistan', ar: 'باكستان', flag: '🇵🇰' },
        { en: 'Bangladesh', ar: 'بنغلاديش', flag: '🇧🇩' },
        { en: 'Sri Lanka', ar: 'سريلانكا', flag: '🇱🇰' },
        { en: 'Nepal', ar: 'نيبال', flag: '🇳🇵' },
        { en: 'Bhutan', ar: 'بوتان', flag: '🇧🇹' },
        { en: 'Maldives', ar: 'جزر المالديف', flag: '🇲🇻' },
        { en: 'Afghanistan', ar: 'أفغانستان', flag: '🇦🇫' },
        { en: 'Iran', ar: 'إيران', flag: '🇮🇷' },
        { en: 'South Sudan', ar: 'جنوب السودان', flag: '🇸🇸' },
        { en: 'Ethiopia', ar: 'إثيوبيا', flag: '🇪🇹' },
        { en: 'Eritrea', ar: 'إريتريا', flag: '🇪🇷' },
        { en: 'Kenya', ar: 'كينيا', flag: '🇰🇪' },
        { en: 'Tanzania', ar: 'تنزانيا', flag: '🇹🇿' },
        { en: 'Uganda', ar: 'أوغندا', flag: '🇺🇬' },
        { en: 'Rwanda', ar: 'رواندا', flag: '🇷🇼' },
        { en: 'Burundi', ar: 'بوروندي', flag: '🇧🇮' },
        { en: 'Democratic Republic of Congo', ar: 'جمهورية الكونغو الديمقراطية', flag: '🇨🇩' },
        { en: 'Republic of Congo', ar: 'جمهورية الكونغو', flag: '🇨🇬' },
        { en: 'Central African Republic', ar: 'جمهورية أفريقيا الوسطى', flag: '🇨🇫' },
        { en: 'Chad', ar: 'تشاد', flag: '🇹🇩' },
        { en: 'Niger', ar: 'النيجر', flag: '🇳🇪' },
        { en: 'Mali', ar: 'مالي', flag: '🇲🇱' },
        { en: 'Burkina Faso', ar: 'بوركينا فاسو', flag: '🇧🇫' },
        { en: 'Senegal', ar: 'السنغال', flag: '🇸🇳' },
        { en: 'Gambia', ar: 'غامبيا', flag: '🇬🇲' },
        { en: 'Guinea-Bissau', ar: 'غينيا بيساو', flag: '🇬🇼' },
        { en: 'Guinea', ar: 'غينيا', flag: '🇬🇳' },
        { en: 'Sierra Leone', ar: 'سيراليون', flag: '🇸🇱' },
        { en: 'Liberia', ar: 'ليبيريا', flag: '🇱🇷' },
        { en: 'Ivory Coast', ar: 'ساحل العاج', flag: '🇨🇮' },
        { en: 'Ghana', ar: 'غانا', flag: '🇬🇭' },
        { en: 'Togo', ar: 'توغو', flag: '🇹🇬' },
        { en: 'Benin', ar: 'بنين', flag: '🇧🇯' },
        { en: 'Nigeria', ar: 'نيجيريا', flag: '🇳🇬' },
        { en: 'Cameroon', ar: 'الكاميرون', flag: '🇨🇲' },
        { en: 'Gabon', ar: 'الغابون', flag: '🇬🇦' },
        { en: 'Equatorial Guinea', ar: 'غينيا الاستوائية', flag: '🇬🇶' },
        { en: 'Sao Tome and Principe', ar: 'ساو تومي وبرينسيبي', flag: '🇸🇹' },
        { en: 'Angola', ar: 'أنغولا', flag: '🇦🇴' },
        { en: 'Namibia', ar: 'ناميبيا', flag: '🇳🇦' },
        { en: 'Botswana', ar: 'بوتسوانا', flag: '🇧🇼' },
        { en: 'Zimbabwe', ar: 'زيمبابوي', flag: '🇿🇼' },
        { en: 'Zambia', ar: 'زامبيا', flag: '🇿🇲' },
        { en: 'Malawi', ar: 'ملاوي', flag: '🇲🇼' },
        { en: 'Mozambique', ar: 'موزمبيق', flag: '🇲🇿' },
        { en: 'Madagascar', ar: 'مدغشقر', flag: '🇲🇬' },
        { en: 'Mauritius', ar: 'موريشيوس', flag: '🇲🇺' },
        { en: 'Seychelles', ar: 'سيشل', flag: '🇸🇨' },
        { en: 'Mayotte', ar: 'مايوت', flag: '🇾🇹' },
        { en: 'Reunion', ar: 'ريونيون', flag: '🇷🇪' },
        { en: 'South Africa', ar: 'جنوب أفريقيا', flag: '🇿🇦' },
        { en: 'Lesotho', ar: 'ليسوتو', flag: '🇱🇸' },
        { en: 'Eswatini', ar: 'إسواتيني', flag: '🇸🇿' },
        { en: 'Brazil', ar: 'البرازيل', flag: '🇧🇷' },
        { en: 'Argentina', ar: 'الأرجنتين', flag: '🇦🇷' },
        { en: 'Chile', ar: 'تشيلي', flag: '🇨🇱' },
        { en: 'Peru', ar: 'بيرو', flag: '🇵🇪' },
        { en: 'Bolivia', ar: 'بوليفيا', flag: '🇧🇴' },
        { en: 'Paraguay', ar: 'باراغواي', flag: '🇵🇾' },
        { en: 'Uruguay', ar: 'أوروغواي', flag: '🇺🇾' },
        { en: 'Colombia', ar: 'كولومبيا', flag: '🇨🇴' },
        { en: 'Venezuela', ar: 'فنزويلا', flag: '🇻🇪' },
        { en: 'Ecuador', ar: 'الإكوادور', flag: '🇪🇨' },
        { en: 'Guyana', ar: 'غيانا', flag: '🇬🇾' },
        { en: 'Suriname', ar: 'سورينام', flag: '🇸🇷' },
        { en: 'French Guiana', ar: 'غيانا الفرنسية', flag: '🇬🇫' },
        { en: 'Mexico', ar: 'المكسيك', flag: '🇲🇽' },
        { en: 'Guatemala', ar: 'غواتيمالا', flag: '🇬🇹' },
        { en: 'Belize', ar: 'بليز', flag: '🇧🇿' },
        { en: 'El Salvador', ar: 'السلفادور', flag: '🇸🇻' },
        { en: 'Honduras', ar: 'هندوراس', flag: '🇭🇳' },
        { en: 'Nicaragua', ar: 'نيكاراغوا', flag: '🇳🇮' },
        { en: 'Costa Rica', ar: 'كوستاريكا', flag: '🇨🇷' },
        { en: 'Panama', ar: 'بنما', flag: '🇵🇦' },
        { en: 'Cuba', ar: 'كوبا', flag: '🇨🇺' },
        { en: 'Jamaica', ar: 'جامايكا', flag: '🇯🇲' },
        { en: 'Haiti', ar: 'هايتي', flag: '🇭🇹' },
        { en: 'Dominican Republic', ar: 'جمهورية الدومينيكان', flag: '🇩🇴' },
        { en: 'Puerto Rico', ar: 'بورتوريكو', flag: '🇵🇷' },
        { en: 'Trinidad and Tobago', ar: 'ترينيداد وتوباغو', flag: '🇹🇹' },
        { en: 'Barbados', ar: 'بربادوس', flag: '🇧🇧' },
        { en: 'Grenada', ar: 'غرينادا', flag: '🇬🇩' },
        { en: 'St Vincent', ar: 'سانت فنسنت', flag: '🇻🇨' },
        { en: 'St Lucia', ar: 'سانت لوسيا', flag: '🇱🇨' },
        { en: 'Dominica', ar: 'دومينيكا', flag: '🇩🇲' },
        { en: 'Antigua and Barbuda', ar: 'أنتيغوا وبربودا', flag: '🇦🇬' },
        { en: 'St Kitts and Nevis', ar: 'سانت كيتس ونيفيس', flag: '🇰🇳' },
        { en: 'Bahamas', ar: 'جزر البهاما', flag: '🇧🇸' },
        { en: 'Cayman Islands', ar: 'جزر كايمان', flag: '🇰🇾' },
        { en: 'Turks and Caicos', ar: 'جزر توركس وكايكوس', flag: '🇹🇨' },
        { en: 'British Virgin Islands', ar: 'جزر فيرجن البريطانية', flag: '🇻🇬' },
        { en: 'US Virgin Islands', ar: 'جزر فيرجن الأمريكية', flag: '🇻🇮' },
        { en: 'Anguilla', ar: 'أنغويلا', flag: '🇦🇮' },
        { en: 'Montserrat', ar: 'مونتسيرات', flag: '🇲🇸' },
        { en: 'Bermuda', ar: 'برمودا', flag: '🇧🇲' },
        { en: 'Falkland Islands', ar: 'جزر فوكلاند', flag: '🇫🇰' },
        { en: 'South Georgia', ar: 'جورجيا الجنوبية', flag: '🇬🇸' },
        { en: 'Antarctica', ar: 'أنتاركتيكا', flag: '🇦🇶' }
    ];

    // Filter countries based on search
    const filteredCountries = countries.filter(country =>
        country.en.toLowerCase().includes(countrySearch.toLowerCase()) ||
        country.ar.includes(countrySearch)
    );

    // Get current country display info
    const getCurrentCountryDisplay = () => {
        const currentCountry = countries.find(c => c.en === form.country);
        if (currentCountry) {
            return `${currentCountry.ar} ${currentCountry.flag}`;
        }
        return form.country || 'اختر البلد';
    };

    // 1. Redirect if not authenticated
    useEffect(() => {

        // !! to enable update ACf fields you should add it in Functions.php too in WP theme
        setForm({
            name: user.name,
            description: user.description,
            email: user.email,
            password: '',
            user_job: user.user_job,
            user_company: user.user_company,
            country: user.country,
            cert_name: user.cert_name,
            user_linkedin_profile: user.user_linkedin_profile,
            behance: user.behance,
            user_extra_url: user.user_extra_url,
            user_mobile: user.user_mobile,
            private_profile: user.private_profile === true || user.private_profile === 'true' || user.private_profile === 1
        });
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showCountryDropdown && !event.target.closest('.position-relative')) {
                setShowCountryDropdown(false);
                setCountrySearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCountryDropdown]);

    // set user avatar to local storage
    useEffect(() => {
        const localUser = localStorage.getItem('user');

        console.log('inside profile ');
        // Step 2: Parse it to an object
        let userObj = JSON.parse(localUser)
        // Step 3: Modify the object
        !userObj.avatar && user?.avatar_id ? (userObj.avatar = user?.avatar_id?.sizes?.thumbnail) : null; // you can change the key and value
        // Step 4: Save it back
        localStorage.setItem('user', JSON.stringify(userObj))
    }, [user])

    useEffect(() => {
        user?.acf?.avatar_id?.sizes?.thumbnail ? setAvatar(user.acf.avatar_id?.sizes?.thumbnail) : null;
    }, [user]);

    // Fetch user content when user is available
    useEffect(() => {
        if (user?.id || user?.ID) {
            fetchUserContent(user.id || user.ID);
        }
    }, [user]);

    // Function to fetch user content
    const fetchUserContent = async (userId) => {
        setLoading(true);
        try {
            console.log('Fetching user content for userId:', userId);

            const contentTypes = [
                { key: 'articles', type: 'posts' },
                { key: 'plugins', type: 'figplg' },
                { key: 'templates', type: 'figtemp' },
                { key: 'challenges', type: 'challenge' },
                { key: 'podcasts', type: 'podcast' },
                { key: 'certificates', type: 'certificate' },
                { key: 'workSamples', type: 'project' },
                { key: 'jobs', type: 'job' }
            ];

            const contentPromises = contentTypes.map(async ({ key, type }) => {
                try {
                    console.log(`Fetching ${key} for user ${userId}...`);
                    // For admin users, fetch all statuses including drafts and pending
                    const params = isAdminUser || isCommAdminUser
                        ? `author=${userId}&_embed&status=publish,pending,draft`
                        : `author=${userId}&_embed`;
                    const data = await getData(type, params);
                    console.log(`Fetched ${key}:`, data?.length || 0, 'items');
                    return { key, data: Array.isArray(data) ? data : [] };
                } catch (error) {
                    console.error(`Error fetching ${key}:`, error);
                    return { key, data: [] };
                }
            });

            const results = await Promise.all(contentPromises);
            const newUserContent = { ...userContent };
            results.forEach(({ key, data }) => {
                newUserContent[key] = data;
            });

            // Fetch favorites/bookmarks
            try {
                const favoritesData = await getData('favorites', `user=${userId}&_embed`);
                newUserContent.favorites = Array.isArray(favoritesData) ? favoritesData : [];
            } catch (error) {
                console.error('Error fetching favorites:', error);
                newUserContent.favorites = [];
            }

            console.log('Final user content:', newUserContent);
            setUserContent(newUserContent);
        } catch (error) {
            console.error('Error fetching user content:', error);
        } finally {
            setLoading(false);
        }
    };

    // Function to handle post status changes (for commadmin users)
    const handlePostStatusChange = async (postId, newStatus, contentType = 'posts') => {
        if (!isCommAdminUser) return;

        try {
            setLoading(true);

            // Map content types to their API endpoints
            const contentTypeMap = {
                'articles': 'posts',
                'plugins': 'figplg',
                'templates': 'figtemp',
                'challenges': 'challenge',
                'podcasts': 'podcast',
                'certificates': 'certificate',
                'workSamples': 'project',
                'jobs': 'job'
            };

            const apiType = contentTypeMap[contentType] || 'posts';

            // This would need to be implemented in the API
            // For now, we'll show a toast message and simulate the change
            const statusText = {
                'publish': 'منشور',
                'pending': 'في الانتظار',
                'draft': 'مسودة'
            };

            toast.success(`تم تغيير حالة المحتوى إلى ${statusText[newStatus] || newStatus}`);

            // Update the local state immediately for better UX
            setUserContent(prevContent => {
                const updatedContent = { ...prevContent };
                if (updatedContent[contentType]) {
                    updatedContent[contentType] = updatedContent[contentType].map(post =>
                        post.id === postId ? { ...post, status: newStatus } : post
                    );
                }
                return updatedContent;
            });

            // In a real implementation, you would make an API call here:
            // const response = await updatePostStatus(apiType, postId, newStatus);
            // if (!response.success) throw new Error(response.message);

            // Refresh the content to show updated status (optional, since we updated locally)
            // if (user?.id || user?.ID) {
            //     fetchUserContent(user.id || user.ID);
            // }

        } catch (error) {
            console.error('Error changing post status:', error);
            toast.error('حدث خطأ أثناء تغيير حالة المحتوى');

            // Revert the local state change on error
            setUserContent(prevContent => {
                const updatedContent = { ...prevContent };
                if (updatedContent[contentType]) {
                    updatedContent[contentType] = updatedContent[contentType].map(post =>
                        post.id === postId ? { ...post, status: post.originalStatus || post.status } : post
                    );
                }
                return updatedContent;
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = e => {
        const { name, value, files, type, checked } = e.target;

        console.log(name, value, files, type, checked);

        if (name === 'avatar') {
            if (files[0] && files[0].size / 1000 < 1000) {
                setFile(files[0]);
            } else {
                alert('حجم الصورة يجب أن يكون أقل من 1 ميجابايت');
            }
        } else if (name === 'private_profile') {
            setForm(f => ({ ...f, [name]: checked }));
        } else {
            setForm(f => ({ ...f, [name]: value }));
        }
    };

    const handleCountrySelect = (country) => {
        setForm(f => ({ ...f, country: country.en }));
        setCountrySearch('');
        setShowCountryDropdown(false);
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            let meta = {};
            let avatar_id = '';
            // 2. If new avatar selected, upload it first
            if (file && file.size / 1000 < 1000) {
                const media = await uploadAvatar(file, file.name);
                // meta = { avatar_id: media.id };
                // avatar_id = { avatar_id: media.id };
                avatar_id = `${media.id}`; // Convert to string for JSON compatibility
                setAvatar(media.source_url);
                console.log(media.source_url);
                // alert(media.id)
            }

            console.log({ ...form, avatar_id });
            // 3. Update name, email, password & avatar meta
            await updateUser({ ...form, avatar_id });
            console.log('update user');
            console.log(form);

            toast.success('تم تحديث بياناتك بنجاح')
            // alert('تم تحديث بياناتك بنجاح');
            // avatar_id ? location.reload() : null; // Refresh to show updated data
        } catch (err) {
            console.error(err);
            toast.error('حدث خطأ أثناء حفظ البيانات. يرجى المحاولة مرة أخرى.')
            // alert('حدث خطأ أثناء حفظ البيانات. يرجى المحاولة مرة أخرى.');
        } finally {
            setSaving(false);
        }
    };

    // Google Account Linking
    const handleGoogleLink = async (credentialResponse) => {
        try {
            const googleToken = credentialResponse.credential;
            const linkAccount = await loginUserGoogleAllInOne(googleToken);
            console.log(linkAccount);

            toast.success("تم ربط حساب Google بنجاح! يمكنك الآن تسجيل الدخول باستخدام Google One Tap.");

            // Update user object with Google link status
            const parsedToken = JSON.parse(localStorage.getItem('user'));
            const userObject = {
                ...parsedToken,
                linked_with_google: linkAccount['linked_with_google']
            };
            localStorage.setItem('user', JSON.stringify(userObject));

        } catch (error) {
            console.error('Error linking Google account:', error);
            toast.error("فشل ربط حساب Google. يرجى المحاولة مرة أخرى.");
        }
    };

    // Check user role and set permissions
    useEffect(() => {
        if (user?.roles) {
            const roles = Array.isArray(user.roles) ? user.roles : [user.roles];
            setUserRole(roles[0] || 'subscriber');
            setIsAdminUser(roles.includes('administrator'));
            setIsCommAdminUser(roles.includes('commadmin'));
        }
    }, [user]);

    // Function to render content type content with status management
    const renderContentTypeContent = (contentType, content, sectionPath) => {
        if (!content || content.length === 0) {
            return (
                <div className="alert alert-info">
                    <i className="bi bi-info-circle"></i>
                    لا توجد {contentType === 'articles' ? 'مقالات' :
                        contentType === 'plugins' ? 'إضافات' :
                            contentType === 'templates' ? 'قوالب' :
                                contentType === 'challenges' ? 'تحديات' :
                                    contentType === 'podcasts' ? 'بودكاست' :
                                        contentType === 'certificates' ? 'شهادات' :
                                            contentType === 'workSamples' ? 'نماذج أعمال' :
                                                contentType === 'jobs' ? 'إعلانات الوظائف' : 'محتوى'} حالياً.
                </div>
            );
        }

        // Group content by status
        const publishedContent = content.filter(post => post.status === 'publish');
        const pendingContent = content.filter(post => post.status === 'pending');
        const draftContent = content.filter(post => post.status === 'draft');

        return (
            <>
                {/* Status Summary */}
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-4">
                        <div className="card bg-success text-white">
                            <div className="card-body text-center">
                                <i className="bi bi-check-circle fs-1"></i>
                                <h5>{publishedContent.length}</h5>
                                <small>منشور</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="card bg-warning text-dark">
                            <div className="card-body text-center">
                                <i className="bi bi-clock fs-1"></i>
                                <h5>{pendingContent.length}</h5>
                                <small>في الانتظار</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="card bg-secondary text-white">
                            <div className="card-body text-center">
                                <i className="bi bi-file-earmark fs-1"></i>
                                <h5>{draftContent.length}</h5>
                                <small>مسودة</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Tabs */}
                <ul className="nav nav-pills mb-3" id={`${contentType}StatusTabs`} role="tablist">
                    <li className="nav-item" role="presentation">
                        <button className="nav-link active" id={`${contentType}-published-tab`} data-bs-toggle="pill" data-bs-target={`#${contentType}-published`} type="button" role="tab">
                            <i className="bi bi-check-circle text-success me-1"></i>
                            منشور ({publishedContent.length})
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button className="nav-link" id={`${contentType}-pending-tab`} data-bs-toggle="pill" data-bs-target={`#${contentType}-pending`} type="button" role="tab">
                            <i className="bi bi-clock text-warning me-1"></i>
                            في الانتظار ({pendingContent.length})
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button className="nav-link" id={`${contentType}-draft-tab`} data-bs-toggle="pill" data-bs-target={`#${contentType}-draft`} type="button" role="tab">
                            <i className="bi bi-file-earmark text-secondary me-1"></i>
                            مسودة ({draftContent.length})
                        </button>
                    </li>
                </ul>

                {/* Status Tab Content */}
                <div className="tab-content" id={`${contentType}StatusTabsContent`}>
                    {/* Published Content */}
                    <div className="tab-pane fade show active" id={`${contentType}-published`} role="tabpanel">
                        <h6 className="mb-3">المحتوى المنشور</h6>
                        {publishedContent.length > 0 ? (
                            <div className="row g-3">
                                {publishedContent.slice(0, 6).map((post) => (
                                    <div key={post.id} className="col-12 col-md-6 col-lg-4">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className="badge bg-success">منشور</span>
                                                    {isCommAdminUser && (
                                                        <button
                                                            className="btn btn-outline-warning btn-sm"
                                                            onClick={() => handlePostStatusChange(post.id, 'pending', contentType)}
                                                            title="إلغاء النشر"
                                                        >
                                                            <i className="bi bi-eye-slash"></i>
                                                        </button>
                                                    )}
                                                </div>
                                                <h6 className="card-title">{post.title?.rendered || post.post_title}</h6>
                                                <p className="card-text small text-muted">
                                                    {post.excerpt?.rendered ?
                                                        post.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                        'لا يوجد وصف'
                                                    }
                                                </p>
                                                <div className="d-flex gap-2">
                                                    <Link href={`/sections/${sectionPath}/${post.id}`} className="btn btn-outline-primary btn-sm">
                                                        <i className="bi bi-eye me-1"></i>
                                                        عرض
                                                    </Link>
                                                    <Link href={`/sections/${sectionPath}/${post.id}?edit=true`} className="btn btn-outline-secondary btn-sm">
                                                        <i className="bi bi-pencil me-1"></i>
                                                        تعديل
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i>
                                لا توجد محتويات منشورة حالياً.
                            </div>
                        )}
                    </div>

                    {/* Pending Content */}
                    <div className="tab-pane fade" id={`${contentType}-pending`} role="tabpanel">
                        <h6 className="mb-3">المحتوى في الانتظار</h6>
                        {pendingContent.length > 0 ? (
                            <div className="row g-3">
                                {pendingContent.slice(0, 6).map((post) => (
                                    <div key={post.id} className="col-12 col-md-6 col-lg-4">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className="badge bg-warning text-dark">في الانتظار</span>
                                                    {isCommAdminUser && (
                                                        <button
                                                            className="btn btn-outline-success btn-sm"
                                                            onClick={() => handlePostStatusChange(post.id, 'publish', contentType)}
                                                            title="نشر المحتوى"
                                                        >
                                                            <i className="bi bi-check-circle"></i>
                                                        </button>
                                                    )}
                                                </div>
                                                <h6 className="card-title">{post.title?.rendered || post.post_title}</h6>
                                                <p className="card-text small text-muted">
                                                    {post.excerpt?.rendered ?
                                                        post.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                        'لا يوجد وصف'
                                                    }
                                                </p>
                                                <div className="d-flex gap-2">
                                                    <Link href={`/sections/${sectionPath}/${post.id}`} className="btn btn-outline-primary btn-sm">
                                                        <i className="bi bi-eye me-1"></i>
                                                        معاينة
                                                    </Link>
                                                    <Link href={`/sections/${sectionPath}/${post.id}?edit=true`} className="btn btn-outline-secondary btn-sm">
                                                        <i className="bi bi-pencil me-1"></i>
                                                        تعديل
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i>
                                لا توجد محتويات في الانتظار حالياً.
                            </div>
                        )}
                    </div>

                    {/* Draft Content */}
                    <div className="tab-pane fade" id={`${contentType}-draft`} role="tabpanel">
                        <h6 className="mb-3">المسودات</h6>
                        {draftContent.length > 0 ? (
                            <div className="row g-3">
                                {draftContent.slice(0, 6).map((post) => (
                                    <div key={post.id} className="col-12 col-md-6 col-lg-4">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className="badge bg-secondary">مسودة</span>
                                                    {isCommAdminUser && (
                                                        <button
                                                            className="btn btn-outline-success btn-sm"
                                                            onClick={() => handlePostStatusChange(post.id, 'pending', contentType)}
                                                            title="إرسال للمراجعة"
                                                        >
                                                            <i className="bi bi-send"></i>
                                                        </button>
                                                    )}
                                                </div>
                                                <h6 className="card-title">{post.title?.rendered || post.post_title}</h6>
                                                <p className="card-text small text-muted">
                                                    {post.excerpt?.rendered ?
                                                        post.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                        'لا يوجد وصف'
                                                    }
                                                </p>
                                                <div className="d-flex gap-2">
                                                    <Link href={`/sections/${sectionPath}/${post.id}`} className="btn btn-outline-primary btn-sm">
                                                        <i className="bi bi-eye me-1"></i>
                                                        معاينة
                                                    </Link>
                                                    <Link href={`/sections/${sectionPath}/${post.id}?edit=true`} className="btn btn-outline-secondary btn-sm">
                                                        <i className="bi bi-pencil me-1"></i>
                                                        تعديل
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i>
                                لا توجد مسودات حالياً.
                            </div>
                        )}
                    </div>
                </div>

                {/* View All Button */}
                <div className="mt-3">
                    <Link href={`/sections/${sectionPath}`} className="btn btn-outline-dark">
                        <i className="bi bi-arrow-left"></i> عرض جميع {contentType === 'articles' ? 'المقالات' :
                            contentType === 'plugins' ? 'الإضافات' :
                                contentType === 'templates' ? 'القوالب' :
                                    contentType === 'challenges' ? 'التحديات' :
                                        contentType === 'podcasts' ? 'البودكاست' :
                                            contentType === 'certificates' ? 'الشهادات' :
                                                contentType === 'workSamples' ? 'نماذج الأعمال' :
                                                    contentType === 'jobs' ? 'إعلانات الوظائف' : 'المحتوى'}
                    </Link>
                </div>
            </>
        );
    };

    if (!user) return <div className='mx-auto my-5 bg-white text-center col-auto border rounded-4 py-3 px-4'>جار جلب البيانات ... <div className="spinner-border" role="status">
        <span className="sr-only"></span>
    </div></div>;

    return (
        <>
            {activeSection === 'myLinks' && (
                <MyShortLinks />
            )}


            {activeSection === 'googleLink' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <h3 className="mb-4">ربط حساب Google</h3>
                    <p className="text-muted mb-4">
                        ربط حساب Google الخاص بك يسمح لك بتسجيل الدخول بسهولة باستخدام Google One Tap.
                    </p>

                    <div className="alert alert-info">
                        <strong>ملاحظة:</strong> يجب أن يكون البريد الإلكتروني في حساب Google مطابقاً للبريد الإلكتروني في حسابك الحالي.
                    </div>

                    <div className="mt-4">
                        <GoogleOAuthProvider clientId="972759779991-rtjp4vb13bosiv282b7nmc8tas99okae.apps.googleusercontent.com">
                            <div className="w-100">
                                <GoogleLogin
                                    onSuccess={handleGoogleLink}
                                    onError={() => toast.error("فشل ربط حساب Google")}
                                    useOneTap={false}
                                    text="link_with"
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </GoogleOAuthProvider>
                    </div>

                    <div className="mt-4">
                        <h5>حالة الربط:</h5>
                        <p className="text-success">
                            <i className="bi bi-check-circle"></i>
                            {user?.email} - جاهز للربط مع Google
                        </p>
                    </div>
                </div>
            )}
            {activeSection === 'appleWallet' && (
                (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_SHOW_APPLE_WALLET === 'true') ? (
                <div className="card rounded-4 shadow-sm p-4">
                    <h3 className="mb-4"><i className="bi bi-wallet2"></i> البطاقة في المحفظة الرقمية</h3>
                    <p className="text-muted mb-4">
                        أضف بطاقة العضوية إلى Apple Wallet (iPhone) أو Google Wallet (Android) للوصول السريع.
                    </p>
                    {user?.slug && (
                        <p className="text-muted small mb-3">
                            <i className="bi bi-person-badge"></i> معرف العضو: <strong>{user.slug}</strong>
                        </p>
                    )}
                    <div className="alert alert-light border mb-4">
                        <small className="text-muted">
                            على iPhone: Apple Wallet. على أندرويد: Google Wallet.
                        </small>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <AddToAppleWalletButton user={user} />
                        <AddToGoogleWalletButton user={user} />
                    </div>
                </div>
                ) : (
                <div className="card rounded-4 shadow-sm p-4">
                    <p className="text-muted mb-0">هذه الميزة متاحة في بيئة التطوير فقط.</p>
                </div>
                )
            )}
            {/* Content Management Sections */}
            {activeSection === 'templates' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-file-earmark-text'></i> القوالب</h3>
                        <a href="/sections/templates" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة قالب جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة قوالب فيجما الخاصة بك</p>

                    {/* Enhanced content display for admin users */}
                    {(isAdminUser || isCommAdminUser) && userContent.templates.length > 0 ? (
                        <>
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i>
                                {isAdminUser
                                    ? 'بصفتك مدير، يمكنك إدارة جميع القوالب الخاصة بك مع صلاحيات كاملة'
                                    : 'بصفتك مشرف مجتمع، يمكنك إدارة قوالبك ونشرها أو إلغاء نشرها'
                                }
                            </div>

                            {/* Status Summary */}
                            <div className="row g-3 mb-4">
                                <div className="col-12 col-md-4">
                                    <div className="card bg-success text-white">
                                        <div className="card-body text-center">
                                            <i className="bi bi-check-circle fs-1"></i>
                                            <h5>{userContent.templates.filter(template => template.status === 'publish').length}</h5>
                                            <small>منشور</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="card bg-warning text-dark">
                                        <div className="card-body text-center">
                                            <i className="bi bi-clock fs-1"></i>
                                            <h5>{userContent.templates.filter(template => template.status === 'pending').length}</h5>
                                            <small>في الانتظار</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="card bg-secondary text-white">
                                        <div className="card-body text-center">
                                            <i className="bi bi-file-earmark fs-1"></i>
                                            <h5>{userContent.templates.filter(template => template.status === 'draft').length}</h5>
                                            <small>مسودة</small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Templates List with Status */}
                            <div className="row g-3">
                                {userContent.templates.slice(0, 6).map((template) => (
                                    <div key={template.id} className="col-12 col-md-6 col-lg-4">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className={`badge ${template.status === 'publish' ? 'bg-success' :
                                                        template.status === 'pending' ? 'bg-warning text-dark' :
                                                            'bg-secondary'
                                                        }`}>
                                                        {template.status === 'publish' ? 'منشور' :
                                                            template.status === 'pending' ? 'في الانتظار' : 'مسودة'}
                                                    </span>
                                                    {(isCommAdminUser || isAdminUser) && (
                                                        <div className="dropdown">
                                                            <button className="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                                                <i className="bi bi-gear"></i>
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                                {template.status === 'draft' && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(template.id, 'pending', 'templates')}
                                                                        >
                                                                            <i className="bi bi-send me-2"></i>
                                                                            إرسال للمراجعة
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                {template.status === 'pending' && isCommAdminUser && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(template.id, 'publish', 'templates')}
                                                                        >
                                                                            <i className="bi bi-check-circle me-2"></i>
                                                                            نشر
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                {template.status === 'publish' && isCommAdminUser && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(template.id, 'pending', 'templates')}
                                                                        >
                                                                            <i className="bi bi-eye-slash me-2"></i>
                                                                            إلغاء النشر
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                <li><hr className="dropdown-divider" /></li>
                                                                <li>
                                                                    <Link href={`/sections/templates/${template.id}?edit=true`} className="dropdown-item">
                                                                        <i className="bi bi-pencil me-2"></i>
                                                                        تعديل
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                                <h6 className="card-title">{template.title?.rendered || template.post_title}</h6>
                                                <p className="card-text small text-muted">
                                                    {template.excerpt?.rendered ?
                                                        template.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                        'لا يوجد وصف'
                                                    }
                                                </p>
                                                <div className="d-flex gap-2">
                                                    <Link href={`/sections/templates/${template.id}`} className="btn btn-outline-warning btn-sm">
                                                        <i className="bi bi-eye me-1"></i>
                                                        {template.status === 'publish' ? 'عرض' : 'معاينة'}
                                                    </Link>
                                                    {(isAdminUser || isCommAdminUser) && (
                                                        <Link href={`/sections/templates/${template.id}?edit=true`} className="btn btn-outline-secondary btn-sm">
                                                            <i className="bi bi-pencil me-1"></i>
                                                            تعديل
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            يمكنك إضافة قوالب جديدة أو تعديل القوالب الموجودة من خلال صفحة القوالب الرئيسية.
                        </div>
                    )}

                    <div className="mt-3">
                        <a href="/sections/templates" className="btn btn-outline-dark">
                            <i className="bi bi-arrow-left"></i> عرض جميع القوالب
                        </a>
                        {(isAdminUser || isCommAdminUser) && (
                            <Link href="/account?section=myContent" className="btn btn-primary ms-2">
                                <i className="bi bi-gear me-1"></i>
                                إدارة المحتوى المتقدمة
                            </Link>
                        )}
                    </div>
                </div>
            )}
            {activeSection === 'plugins' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-puzzle'></i> الإضافات</h3>
                        <a href="/sections/plugins" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة إضافة جديدة
                        </a>
                    </div>
                    <p className="text-muted">إدارة إضافات فيجما الخاصة بك</p>

                    {/* Enhanced content display for admin users */}
                    {(isAdminUser || isCommAdminUser) && userContent.plugins.length > 0 ? (
                        <>
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i>
                                {isAdminUser
                                    ? 'بصفتك مدير، يمكنك إدارة جميع الإضافات الخاصة بك مع صلاحيات كاملة'
                                    : 'بصفتك مشرف مجتمع، يمكنك إدارة إضافاتك ونشرها أو إلغاء نشرها'
                                }
                            </div>

                            {/* Status Summary */}
                            <div className="row g-3 mb-4">
                                <div className="col-12 col-md-4">
                                    <div className="card bg-success text-white">
                                        <div className="card-body text-center">
                                            <i className="bi bi-check-circle fs-1"></i>
                                            <h5>{userContent.plugins.filter(plugin => plugin.status === 'publish').length}</h5>
                                            <small>منشور</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="card bg-warning text-dark">
                                        <div className="card-body text-center">
                                            <i className="bi bi-clock fs-1"></i>
                                            <h5>{userContent.plugins.filter(plugin => plugin.status === 'pending').length}</h5>
                                            <small>في الانتظار</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="card bg-secondary text-white">
                                        <div className="card-body text-center">
                                            <i className="bi bi-file-earmark fs-1"></i>
                                            <h5>{userContent.plugins.filter(plugin => plugin.status === 'draft').length}</h5>
                                            <small>مسودة</small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Plugins List with Status */}
                            <div className="row g-3">
                                {userContent.plugins.slice(0, 6).map((plugin) => (
                                    <div key={plugin.id} className="col-12 col-md-6 col-lg-4">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className={`badge ${plugin.status === 'publish' ? 'bg-success' :
                                                        plugin.status === 'pending' ? 'bg-warning text-dark' :
                                                            'bg-secondary'
                                                        }`}>
                                                        {plugin.status === 'publish' ? 'منشور' :
                                                            plugin.status === 'pending' ? 'في الانتظار' : 'مسودة'}
                                                    </span>
                                                    {(isCommAdminUser || isAdminUser) && (
                                                        <div className="dropdown">
                                                            <button className="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                                                <i className="bi bi-gear"></i>
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                                {plugin.status === 'draft' && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(plugin.id, 'pending', 'plugins')}
                                                                        >
                                                                            <i className="bi bi-send me-2"></i>
                                                                            إرسال للمراجعة
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                {plugin.status === 'pending' && isCommAdminUser && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(plugin.id, 'publish', 'plugins')}
                                                                        >
                                                                            <i className="bi bi-check-circle me-2"></i>
                                                                            نشر
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                {plugin.status === 'publish' && isCommAdminUser && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(plugin.id, 'pending', 'plugins')}
                                                                        >
                                                                            <i className="bi bi-eye-slash me-2"></i>
                                                                            إلغاء النشر
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                <li><hr className="dropdown-divider" /></li>
                                                                <li>
                                                                    <Link href={`/sections/plugins/${plugin.id}?edit=true`} className="dropdown-item">
                                                                        <i className="bi bi-pencil me-2"></i>
                                                                        تعديل
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                                <h6 className="card-title">{plugin.title?.rendered || plugin.post_title}</h6>
                                                <p className="card-text small text-muted">
                                                    {plugin.excerpt?.rendered ?
                                                        plugin.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                        'لا يوجد وصف'
                                                    }
                                                </p>
                                                <div className="d-flex gap-2">
                                                    <Link href={`/sections/plugins/${plugin.id}`} className="btn btn-outline-success btn-sm">
                                                        <i className="bi bi-eye me-1"></i>
                                                        {plugin.status === 'publish' ? 'عرض' : 'معاينة'}
                                                    </Link>
                                                    {(isAdminUser || isCommAdminUser) && (
                                                        <Link href={`/sections/plugins/${plugin.id}?edit=true`} className="btn btn-outline-secondary btn-sm">
                                                            <i className="bi bi-pencil me-1"></i>
                                                            تعديل
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            يمكنك إضافة إضافات جديدة أو تعديل الإضافات الموجودة من خلال صفحة الإضافات الرئيسية.
                        </div>
                    )}

                    <div className="mt-3">
                        <a href="/sections/plugins" className="btn btn-outline-dark">
                            <i className="bi bi-arrow-left"></i> عرض جميع الإضافات
                        </a>
                        {(isAdminUser || isCommAdminUser) && (
                            <Link href="/account?section=myContent" className="btn btn-primary ms-2">
                                <i className="bi bi-gear me-1"></i>
                                إدارة المحتوى المتقدمة
                            </Link>
                        )}
                    </div>
                </div>
            )}
            {activeSection === 'articles' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-journal-text'></i> المقالات</h3>
                        <a href="/sections/articles" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة مقال جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة المقالات الخاصة بك</p>

                    {/* Enhanced content display for admin users */}
                    {(isAdminUser || isCommAdminUser) && userContent.articles.length > 0 ? (
                        <>
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i>
                                {isAdminUser
                                    ? 'بصفتك مدير، يمكنك إدارة جميع المقالات الخاصة بك مع صلاحيات كاملة'
                                    : 'بصفتك مشرف مجتمع، يمكنك إدارة مقالاتك ونشرها أو إلغاء نشرها'
                                }
                            </div>

                            {/* Status Summary */}
                            <div className="row g-3 mb-4">
                                <div className="col-12 col-md-4">
                                    <div className="card bg-success text-white">
                                        <div className="card-body text-center">
                                            <i className="bi bi-check-circle fs-1"></i>
                                            <h5>{userContent.articles.filter(post => post.status === 'publish').length}</h5>
                                            <small>منشور</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="card bg-warning text-dark">
                                        <div className="card-body text-center">
                                            <i className="bi bi-clock fs-1"></i>
                                            <h5>{userContent.articles.filter(post => post.status === 'pending').length}</h5>
                                            <small>في الانتظار</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="card bg-secondary text-white">
                                        <div className="card-body text-center">
                                            <i className="bi bi-file-earmark fs-1"></i>
                                            <h5>{userContent.articles.filter(post => post.status === 'draft').length}</h5>
                                            <small>مسودة</small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Articles List with Status */}
                            <div className="row g-3">
                                {userContent.articles.slice(0, 6).map((article) => (
                                    <div key={article.id} className="col-12 col-md-6 col-lg-4">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className={`badge ${article.status === 'publish' ? 'bg-success' :
                                                        article.status === 'pending' ? 'bg-warning text-dark' :
                                                            'bg-secondary'
                                                        }`}>
                                                        {article.status === 'publish' ? 'منشور' :
                                                            article.status === 'pending' ? 'في الانتظار' : 'مسودة'}
                                                    </span>
                                                    {(isCommAdminUser || isAdminUser) && (
                                                        <div className="dropdown">
                                                            <button className="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                                                <i className="bi bi-gear"></i>
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                                {article.status === 'draft' && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(article.id, 'pending', 'articles')}
                                                                        >
                                                                            <i className="bi bi-send me-2"></i>
                                                                            إرسال للمراجعة
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                {article.status === 'pending' && isCommAdminUser && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(article.id, 'publish', 'articles')}
                                                                        >
                                                                            <i className="bi bi-check-circle me-2"></i>
                                                                            نشر
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                {article.status === 'publish' && isCommAdminUser && (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handlePostStatusChange(article.id, 'pending', 'articles')}
                                                                        >
                                                                            <i className="bi bi-eye-slash me-2"></i>
                                                                            إلغاء النشر
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                <li><hr className="dropdown-divider" /></li>
                                                                <li>
                                                                    <Link href={`/sections/articles/${article.id}?edit=true`} className="dropdown-item">
                                                                        <i className="bi bi-pencil me-2"></i>
                                                                        تعديل
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                                <h6 className="card-title">{article.title?.rendered || article.post_title}</h6>
                                                <p className="card-text small text-muted">
                                                    {article.excerpt?.rendered ?
                                                        article.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                        'لا يوجد وصف'
                                                    }
                                                </p>
                                                <div className="d-flex gap-2">
                                                    <Link href={`/sections/articles/${article.id}`} className="btn btn-outline-primary btn-sm">
                                                        <i className="bi bi-eye me-1"></i>
                                                        {article.status === 'publish' ? 'عرض' : 'معاينة'}
                                                    </Link>
                                                    {(isAdminUser || isCommAdminUser) && (
                                                        <Link href={`/sections/articles/${article.id}?edit=true`} className="btn btn-outline-secondary btn-sm">
                                                            <i className="bi bi-pencil me-1"></i>
                                                            تعديل
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            يمكنك إضافة مقالات جديدة أو تعديل المقالات الموجودة من خلال صفحة المقالات الرئيسية.
                        </div>
                    )}

                    <div className="mt-3">
                        <a href="/sections/articles" className="btn btn-outline-dark">
                            <i className="bi bi-arrow-left"></i> عرض جميع المقالات
                        </a>
                        {(isAdminUser || isCommAdminUser) && (
                            <Link href="/account?section=myContent" className="btn btn-primary ms-2">
                                <i className="bi bi-gear me-1"></i>
                                إدارة المحتوى المتقدمة
                            </Link>
                        )}
                    </div>
                </div>
            )}
            {activeSection === 'posts' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-file-text'></i> المقالات الشخصية</h3>
                        <a href="/sections/articles" className="btn btn-dark">
                            <i className='bi bi-plus-lg'></i> إضافة مقال جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة المقالات الخاصة بك</p>

                    {/* Enhanced Content Management for Admin and CommAdmin Users */}
                    {(isAdminUser || isCommAdminUser) && userContent?.articles && (
                        <div className="mt-4">
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i>
                                بصفتك {isAdminUser ? 'مدير' : 'مشرف مجتمع'}، يمكنك تعديل حالة مقالاتك ونشرها أو إلغاء نشرها.
                            </div>

                            {renderContentTypeContent('articles', userContent.articles, '/sections/articles')}
                        </div>
                    )}

                    {/* Regular user view */}
                    {(!isAdminUser && !isCommAdminUser) && (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            يمكنك إضافة مقالات جديدة أو تعديل المقالات الموجودة من خلال صفحة المقالات الرئيسية.
                        </div>
                    )}

                    <a href="/sections/articles" className="btn btn-outline-dark">
                        <i className='bi bi-arrow-left'></i> عرض جميع المقالات
                    </a>
                </div>
            )}
            {activeSection === 'jobs' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-briefcase'></i> الوظائف</h3>
                        <a href="/sections/jobs" className="btn btn-dark">
                            <i className='bi bi-plus-lg'></i> إضافة وظيفة جديدة
                        </a>
                    </div>
                    <p className="text-muted">إدارة إعلانات الوظائف</p>

                    {/* Enhanced Content Management for Admin and CommAdmin Users */}
                    {(isAdminUser || isCommAdminUser) && userContent?.jobs && (
                        <div className="mt-4">
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle"></i>
                                بصفتك {isAdminUser ? 'مدير' : 'مشرف مجتمع'}، يمكنك تعديل حالة إعلانات الوظائف الخاصة بك ونشرها أو إلغاء نشرها.
                            </div>

                            {renderContentTypeContent('jobs', userContent.jobs, '/sections/jobs')}
                        </div>
                    )}

                    {/* Regular user view */}
                    {(!isAdminUser && !isCommAdminUser) && (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            يمكنك إضافة إعلانات وظائف جديدة أو تعديل الإعلانات الموجودة من خلال صفحة الوظائف الرئيسية.
                        </div>
                    )}

                    <a href="/sections/jobs" className="btn btn-outline-dark">
                        <i className='bi bi-arrow-left'></i> عرض جميع الوظائف
                    </a>
                </div>
            )}
            {activeSection === 'projects' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-folder'></i> المشاريع</h3>
                        <a href="/sections/projects" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة مشروع جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة المشاريع الخاصة بك</p>
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle"></i>
                        يمكنك إضافة مشاريع جديدة أو تعديل المشاريع الموجودة من خلال صفحة المشاريع الرئيسية.
                    </div>
                    <a href="/sections/projects" className="btn btn-outline-dark">
                        <i className="bi bi-arrow-left"></i> عرض جميع المشاريع
                    </a>
                </div>
            )}
            {activeSection === 'surveys' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-clipboard-data'></i> الاستبيانات</h3>
                        <a href="/sections/surveys" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة استبيان جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة الاستبيانات والاستطلاعات</p>
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle"></i>
                        يمكنك إضافة استبيانات جديدة أو تعديل الاستبيانات الموجودة من خلال صفحة الاستبيانات الرئيسية.
                    </div>
                    <a href="/sections/surveys" className="btn btn-outline-dark">
                        <i className="bi bi-arrow-left"></i> عرض جميع الاستبيانات
                    </a>
                </div>
            )}
            {activeSection === 'podcasts' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-mic'></i> بودكاسات</h3>
                        <a href="/sections/podcast" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة بودكاست جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة البودكاسات الخاصة بك</p>
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle"></i>
                        يمكنك إضافة بودكاسات جديدة أو تعديل البودكاسات الموجودة من خلال صفحة البودكاست الرئيسية.
                    </div>
                    <a href="/sections/podcast" className="btn btn-outline-dark">
                        <i className="bi bi-arrow-left"></i> عرض جميع البودكاسات
                    </a>
                </div>
            )}
            {activeSection === 'videos' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-camera-video'></i> فيديوهات</h3>
                        <a href="/sections/videos" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة فيديو جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة الفيديوهات الخاصة بك</p>
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle"></i>
                        يمكنك إضافة فيديوهات جديدة أو تعديل الفيديوهات الموجودة من خلال صفحة الفيديوهات الرئيسية.
                    </div>
                    <a href="/sections/videos" className="btn btn-outline-dark">
                        <i className="bi bi-arrow-left"></i> عرض جميع الفيديوهات
                    </a>
                </div>
            )}

            {/* Missing Sections */}
            {activeSection === 'challenges' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-trophy'></i> التحديات اليومية</h3>
                        <a href="/sections/challenges" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة تحدي جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة التحديات اليومية ومشاركاتك</p>
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">جاري التحميل...</span>
                            </div>
                        </div>
                    ) : userContent.challenges.length > 0 ? (
                        <div className="row g-3">
                            {userContent.challenges.slice(0, 6).map((challenge) => (
                                <div key={challenge.id} className="col-12 col-md-6 col-lg-4">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body">
                                            <h6 className="card-title">{challenge.title?.rendered || challenge.post_title}</h6>
                                            <p className="card-text small text-muted">
                                                {challenge.excerpt?.rendered ?
                                                    challenge.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                    'لا يوجد وصف'
                                                }
                                            </p>
                                            <Link href={`/sections/challenges/${challenge.id}`} className="btn btn-outline-primary btn-sm">
                                                <i className="bi bi-eye me-1"></i>
                                                عرض التحدي
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            لا توجد تحديات حالياً. يمكنك إضافة تحديات جديدة من خلال صفحة التحديات الرئيسية.
                        </div>
                    )}
                    <div className="mt-3">
                        <a href="/sections/challenges" className="btn btn-outline-dark">
                            <i className="bi bi-arrow-left"></i> عرض جميع التحديات
                        </a>
                    </div>
                </div>
            )}

            {activeSection === 'achievements' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-award'></i> الشهادات والإنجازات</h3>
                        <a href="/sections/certificates" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة شهادة جديدة
                        </a>
                    </div>
                    <p className="text-muted">إدارة شهاداتك وإنجازاتك</p>
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">جاري التحميل...</span>
                            </div>
                        </div>
                    ) : userContent.certificates.length > 0 ? (
                        <div className="row g-3">
                            {userContent.certificates.slice(0, 6).map((certificate) => (
                                <div key={certificate.id} className="col-12 col-md-6 col-lg-4">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body text-center">
                                            <i className="bi bi-award fs-1 text-warning mb-2"></i>
                                            <h6 className="card-title">{certificate.title?.rendered || certificate.post_title}</h6>
                                            <p className="card-text small text-muted">
                                                {certificate.excerpt?.rendered ?
                                                    certificate.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                    'لا يوجد وصف'
                                                }
                                            </p>
                                            <Link href={`/sections/certificates/${certificate.id}`} className="btn btn-outline-warning btn-sm">
                                                <i className="bi bi-eye me-1"></i>
                                                عرض الشهادة
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            لا توجد شهادات أو إنجازات حالياً. يمكنك إضافة شهادات جديدة من خلال صفحة الشهادات الرئيسية.
                        </div>
                    )}
                    <div className="mt-3">
                        <a href="/sections/certificates" className="btn btn-outline-dark">
                            <i className="bi bi-arrow-left"></i> عرض جميع الشهادات
                        </a>
                    </div>
                </div>
            )}

            {activeSection === 'workSamples' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-brush'></i> نماذج أعمالي</h3>
                        <a href="/sections/work-samples" className="btn btn-dark">
                            <i className="bi bi-plus-lg"></i> إضافة نموذج جديد
                        </a>
                    </div>
                    <p className="text-muted">إدارة نماذج أعمالك ومشاريعك</p>
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">جاري التحميل...</span>
                            </div>
                        </div>
                    ) : userContent.workSamples.length > 0 ? (
                        <div className="row g-3">
                            {userContent.workSamples.slice(0, 6).map((sample) => (
                                <div key={sample.id} className="col-12 col-md-6 col-lg-4">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body">
                                            <h6 className="card-title">{sample.title?.rendered || sample.post_title}</h6>
                                            <p className="card-text small text-muted">
                                                {sample.excerpt?.rendered ?
                                                    sample.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                    'لا يوجد وصف'
                                                }
                                            </p>
                                            <Link href={`/sections/work-samples/${sample.id}`} className="btn btn-outline-success btn-sm">
                                                <i className="bi bi-eye me-1"></i>
                                                عرض النموذج
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            لا توجد نماذج أعمال حالياً. يمكنك إضافة نماذج جديدة من خلال صفحة نماذج الأعمال الرئيسية.
                        </div>
                    )}
                    <div className="mt-3">
                        <a href="/sections/work-samples" className="btn btn-outline-dark">
                            <i className="bi bi-arrow-left"></i> عرض جميع النماذج
                        </a>
                    </div>
                </div>
            )}

            {activeSection === 'favorites' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0"><i className='bi bi-bookmark-heart'></i> المفضلة والمحفوظات</h3>
                    </div>
                    <p className="text-muted">إدارة المحتوى المفضل والمحفوظ</p>
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">جاري التحميل...</span>
                            </div>
                        </div>
                    ) : userContent.favorites.length > 0 ? (
                        <div className="row g-3">
                            {userContent.favorites.slice(0, 6).map((favorite) => (
                                <div key={favorite.id} className="col-12 col-md-6 col-lg-4">
                                    <div className="card h-100 border-0 shadow-sm">
                                        <div className="card-body">
                                            <h6 className="card-title">{favorite.title?.rendered || favorite.post_title}</h6>
                                            <p className="card-text small text-muted">
                                                {favorite.excerpt?.rendered ?
                                                    favorite.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 100) + '...' :
                                                    'لا يوجد وصف'
                                                }
                                            </p>
                                            <Link href={`/sections/${favorite.type || 'articles'}/${favorite.id}`} className="btn btn-outline-danger btn-sm">
                                                <i className="bi bi-eye me-1"></i>
                                                عرض المحتوى
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            لا توجد محتويات محفوظة حالياً. يمكنك حفظ المحتويات المفضلة من خلال تصفح الموقع.
                        </div>
                    )}
                </div>
            )}

            {/* Enhanced Content Management for Admin and CommAdmin Users */}
            {(isAdminUser || isCommAdminUser) && activeSection === 'myContent' && (
                <div className="card rounded-4 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="mb-0">
                            <i className='bi bi-gear'></i>
                            {isAdminUser ? 'إدارة المحتوى - مدير' : 'إدارة المحتوى - مشرف مجتمع'}
                        </h3>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => setActiveSection('templates')}
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                إنشاء قالب جديد
                            </button>
                            <button
                                className="btn btn-outline-success"
                                onClick={() => setActiveSection('plugins')}
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                إنشاء إضافة جديدة
                            </button>
                            <button
                                className="btn btn-outline-info"
                                onClick={() => setActiveSection('articles')}
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                إنشاء مقال جديد
                            </button>
                            <button
                                className="btn btn-outline-warning"
                                onClick={() => setActiveSection('jobs')}
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                إنشاء وظيفة جديدة
                            </button>
                        </div>
                    </div>

                    <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>معلومات:</strong> يمكنك إدارة جميع أنواع المحتوى الخاصة بك من هذه الصفحة.
                        استخدم الأزرار أعلاه لإنشاء محتوى جديد أو استخدم التبويبات أدناه لإدارة المحتوى الموجود.
                    </div>

                    {/* Content Type Tabs */}
                    <ul className="nav nav-tabs mb-4" id="contentTypeTabs" role="tablist">
                        <li className="nav-item" role="presentation">
                            <button className="nav-link active" id="articles-tab" data-bs-toggle="tab" data-bs-target="#articles" type="button" role="tab">
                                <i className="bi bi-journal-text text-primary me-1"></i>
                                المقالات ({userContent.articles.length})
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="plugins-tab" data-bs-toggle="tab" data-bs-target="#plugins" type="button" role="tab">
                                <i className="bi bi-puzzle text-success me-1"></i>
                                الإضافات ({userContent.plugins.length})
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="templates-tab" data-bs-toggle="tab" data-bs-target="#templates" type="button" role="tab">
                                <i className="bi bi-file-earmark-text text-warning me-1"></i>
                                القوالب ({userContent.templates.length})
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="challenges-tab" data-bs-toggle="tab" data-bs-target="#challenges" type="button" role="tab">
                                <i className="bi bi-trophy text-info me-1"></i>
                                التحديات ({userContent.challenges.length})
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="podcasts-tab" data-bs-toggle="tab" data-bs-target="#podcasts" type="button" role="tab">
                                <i className="bi bi-mic text-danger me-1"></i>
                                البودكاست ({userContent.podcasts.length})
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="certificates-tab" data-bs-toggle="tab" data-bs-target="#certificates" type="button" role="tab">
                                <i className="bi bi-award text-warning me-1"></i>
                                الشهادات ({userContent.certificates.length})
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="workSamples-tab" data-bs-toggle="tab" data-bs-target="#workSamples" type="button" role="tab">
                                <i className="bi bi-brush text-success me-1"></i>
                                نماذج الأعمال ({userContent.workSamples.length})
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="jobs-tab" data-bs-toggle="tab" data-bs-target="#jobs" type="button" role="tab">
                                <i className="bi bi-briefcase text-primary me-1"></i>
                                الوظائف ({userContent.jobs.length})
                            </button>
                        </li>
                    </ul>

                    {/* Content Type Tabs Content */}
                    <div className="tab-content" id="contentTypeTabsContent">
                        {/* Articles Tab */}
                        <div className="tab-pane fade show active" id="articles" role="tabpanel">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">المقالات</h5>
                                <Link href="/sections/articles?addNew=true" className="btn btn-primary btn-sm">
                                    <i className="bi bi-plus-lg me-1"></i>
                                    مقال جديد
                                </Link>
                            </div>
                            {renderContentTypeContent('articles', userContent.articles, 'articles')}
                        </div>

                        {/* Plugins Tab */}
                        <div className="tab-pane fade" id="plugins" role="tabpanel">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">الإضافات</h5>
                                <Link href="/sections/plugins?addNew=true" className="btn btn-success btn-sm">
                                    <i className="bi bi-plus-lg me-1"></i>
                                    إضافة جديدة
                                </Link>
                            </div>
                            {renderContentTypeContent('plugins', userContent.plugins, 'plugins')}
                        </div>

                        {/* Templates Tab */}
                        <div className="tab-pane fade" id="templates" role="tabpanel">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">القوالب</h5>
                                <Link href="/sections/templates?addNew=true" className="btn btn-warning btn-sm">
                                    <i className="bi bi-plus-lg me-1"></i>
                                    قالب جديد
                                </Link>
                            </div>
                            {renderContentTypeContent('templates', userContent.templates, 'templates')}
                        </div>

                        {/* Challenges Tab */}
                        <div className="tab-pane fade" id="challenges" role="tabpanel">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">التحديات</h5>
                                <Link href="/sections/challenges?addNew=true" className="btn btn-info btn-sm">
                                    <i className="bi bi-plus-lg me-1"></i>
                                    تحدي جديد
                                </Link>
                            </div>
                            {renderContentTypeContent('challenges', userContent.challenges, 'challenges')}
                        </div>

                        {/* Podcasts Tab */}
                        <div className="tab-pane fade" id="podcasts" role="tabpanel">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">البودكاست</h5>
                                <Link href="/sections/podcast?addNew=true" className="btn btn-danger btn-sm">
                                    <i className="bi bi-plus-lg me-1"></i>
                                    بودكاست جديد
                                </Link>
                            </div>
                            {renderContentTypeContent('podcasts', userContent.podcasts, 'podcast')}
                        </div>

                        {/* Certificates Tab */}
                        <div className="tab-pane fade" id="certificates" role="tabpanel">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">الشهادات</h5>
                                <Link href="/sections/certificates?addNew=true" className="btn btn-warning btn-sm">
                                    <i className="bi bi-plus-lg me-1"></i>
                                    شهادة جديدة
                                </Link>
                            </div>
                            {renderContentTypeContent('certificates', userContent.certificates, 'certificates')}
                        </div>

                        {/* Work Samples Tab */}
                        <div className="tab-pane fade" id="workSamples" role="tabpanel">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">نماذج الأعمال</h5>
                                <Link href="/sections/work-samples?addNew=true" className="btn btn-success btn-sm">
                                    <i className="bi bi-plus-lg me-1"></i>
                                    نموذج جديد
                                </Link>
                            </div>
                            {renderContentTypeContent('workSamples', userContent.workSamples, 'work-samples')}
                        </div>

                        {/* Jobs Tab */}
                        <div className="tab-pane fade" id="jobs" role="tabpanel">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">الوظائف</h5>
                                <Link href="/sections/jobs?addNew=true" className="btn btn-primary btn-sm">
                                    <i className="bi bi-plus-lg me-1"></i>
                                    وظيفة جديدة
                                </Link>
                            </div>
                            {renderContentTypeContent('jobs', userContent.jobs, 'jobs')}
                        </div>
                    </div>
                </div>
            )}

            {user && (activeSection === '' || activeSection === 'default' || activeSection === 'accountSettings' || activeSection === 'security' || activeSection === 'privacy') && (
                <form id='accountSettings' onSubmit={handleSubmit} className='userAccountSection'>

                    {form && (

                        <>
                            <div className="card max-w-md p-4 rounded-4 shadow-sm">
                                {/* activeSection { activeSection } */}
                                {(activeSection === 'default' || activeSection === '' || activeSection === 'accountSettings') && (
                                    <>
                                        <h1 className="border-bottom mb-4 pb-3">كل الإعدادات</h1>
                                        {avatar && (
                                            <img src={avatar} alt="avatar" className="w-24 h-24 rounded-circle mb-2" width={96} height={96} />
                                        )}
                                        <div className='col-12 col-md-2'>
                                            <label htmlFor="formFile" className="form-label">إضافة صورة شخصية</label>
                                            <input name="avatar" type="file"
                                                accept="image/*" onChange={handleChange}
                                                className="form-control" id="formFile" />
                                        </div>
                                        <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">
                                            <div className="col-12 mb-4">
                                                <label className='form-label'>الاسم</label>
                                                <input name="name" value={form.name}
                                                    onChange={handleChange}
                                                    className="form-control" />
                                            </div>

                                            <div className="col-12">
                                                <label className='form-label'>نبذة عنك</label>
                                                <textarea name="description"
                                                    onChange={handleChange}
                                                    className="form-control" rows={5} value={form.description}>
                                                    {/* {form.description} */}
                                                </textarea>
                                            </div>
                                        </fieldset>

                                        <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">
                                            <div className="col-12 mb-4">
                                                <label className='form-label'>إسمك في الشهادات</label>
                                                <input name="cert_name" value={form.cert_name}
                                                    onChange={handleChange}
                                                    className="form-control" />
                                            </div>
                                        </fieldset>

                                        <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">

                                            <div className="col-12 mb-4">
                                                <label className='form-label'>الوظيفة الحالية</label>
                                                <input name="user_job" value={form.user_job}
                                                    onChange={handleChange}
                                                    className="form-control" />
                                            </div>


                                            <div className="col-12 mb-4">
                                                <label className='form-label'>الشركة</label>
                                                <input name="user_company" value={form.user_company}
                                                    onChange={handleChange}
                                                    className="form-control" />
                                            </div>


                                        </fieldset>
                                        <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">
                                            <div className="col-12 mb-4">
                                                <label className='form-label'>البلد</label>
                                                <div className="position-relative">
                                                    <div
                                                        className="form-control d-flex justify-content-between align-items-center bg-white"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                                    >
                                                        <span>{getCurrentCountryDisplay()}</span>
                                                        <i className={`bi bi-chevron-${showCountryDropdown ? 'up' : 'down'}`}></i>
                                                    </div>

                                                    {showCountryDropdown && (
                                                        <div className="position-absolute w-100 bg-white border rounded-3 shadow-sm" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                                            <div className="p-2 border-bottom">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="ابحث عن بلدك..."
                                                                    value={countrySearch}
                                                                    onChange={(e) => setCountrySearch(e.target.value)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </div>
                                                            <div className="max-h-60 overflow-y-auto">
                                                                {filteredCountries.map((country, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="p-2 border-bottom d-flex align-items-center"
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => handleCountrySelect(country)}
                                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                                    >
                                                                        <span className="me-2">{country.flag}</span>
                                                                        <span className="me-2">{country.ar}</span>
                                                                        <small className="text-muted">({country.en})</small>
                                                                    </div>
                                                                ))}
                                                                {filteredCountries.length === 0 && (
                                                                    <div className="p-3 text-center text-muted">
                                                                        لا توجد نتائج
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </fieldset>

                                        <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">
                                            <div className="col-12 mb-4">
                                                <label className='form-label'>حساب لينكدإن</label>
                                                <input name="user_linkedin_profile" value={form.user_linkedin_profile}
                                                    onChange={handleChange}
                                                    className="form-control" />
                                            </div>
                                            <div className="col-12 mb-4">
                                                <label className='form-label'>حساب بيهانس</label>
                                                <input name="behance" value={form.behance}
                                                    onChange={handleChange}
                                                    className="form-control" />
                                            </div>

                                            <div className="col-12 mb-4">
                                                <label className='form-label'>رابط اضافي</label>
                                                <input name="user_extra_url" value={form.user_extra_url}
                                                    onChange={handleChange}
                                                    className="form-control" />
                                            </div>


                                        </fieldset>

                                        <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">
                                            <div className="col-12 mb-4">
                                                <label className='form-label'>رقم الموبايل / الجوال</label>
                                                <input type='tel' name="user_mobile" value={form.user_mobile}
                                                    onChange={handleChange}
                                                    className="form-control" />
                                            </div>
                                            <div>
                                                <label className='form-label'>البريد الإلكتروني</label>
                                                <input name="email" type="email" value={form.email}
                                                    onChange={handleChange}
                                                    className="form-control rtl" disabled />
                                                {/* <input name="email" type="email" value={form.email}
                                    onChange={handleChange}
                                    className="form-control  form-control-lg rtl" /> */}
                                            </div>

                                        </fieldset>


                                        {/* <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">

                        <div>
                            <b className='d-block mb-2'>خصوصية الحساب </b>
                            <small>في حال تعيين الحساب إلى " خاص " لن يتمكن الباحثون عن مصممين الوصول لحسابك </small>
                            <div className="form-check form-switch mt-3">
                                <input className="form-check-input" name='private_profile' type="checkbox" id="flexSwitchCheckChecked" onChange={handleChange} checked={form.private_profile === true} />
                                <label className="form-check-label" for="flexSwitchCheckChecked">حساب خاص</label>
                            </div></div>

                        <hr />

                        <div>
                            <label className='form-label'>تغيير كلمة المرور</label>
                            <input name="password" type="password" value={form.password}
                                onChange={handleChange} autoComplete='off'
                                className="form-control" />
                        </div>
                    </fieldset> */}

                                        {/* Content Management Section */}
                                        {/* <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">
                        <h4 className="border-bottom pb-2 mb-3">إدارة المحتوى</h4>
                        
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <i className="bi bi-journal-text fs-1 text-primary mb-2"></i>
                                        <h6 className="card-title">المقالات</h6>
                                        <p className="card-text small text-muted">إدارة مقالاتك ومشاركاتك</p>
                                        <Link href="/sections/articles" className="btn btn-outline-primary btn-sm">
                                            <i className="bi bi-eye me-1"></i>
                                            عرض المقالات
                                        </Link>
                                        <Link href="/sections/articles?addNew=true" className="btn btn-primary btn-sm ms-2">
                                            <i className="bi bi-plus-lg me-1"></i>
                                            إضافة مقال
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <i className="bi bi-puzzle fs-1 text-success mb-2"></i>
                                        <h6 className="card-title">الإضافات</h6>
                                        <p className="card-text small text-muted">إدارة إضافات فيجما الخاصة بك</p>
                                        <Link href="/sections/plugins" className="btn btn-outline-success btn-sm">
                                            <i className="bi bi-eye me-1"></i>
                                            عرض الإضافات
                                        </Link>
                                        <Link href="/sections/plugins?addNew=true" className="btn btn-success btn-sm ms-2">
                                            <i className="bi bi-plus-lg me-1"></i>
                                            إضافة إضافة
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <i className="bi bi-file-earmark-text fs-1 text-warning mb-2"></i>
                                        <h6 className="card-title">القوالب</h6>
                                        <p className="card-text small text-muted">إدارة قوالب فيجما الخاصة بك</p>
                                        <Link href="/sections/templates" className="btn btn-outline-warning btn-sm">
                                            <i className="bi bi-eye me-1"></i>
                                            عرض القوالب
                                        </Link>
                                        <Link href="/sections/templates?addNew=true" className="btn btn-warning btn-sm ms-2">
                                            <i className="bi bi-plus-lg me-1"></i>
                                            إضافة قالب
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <i className="bi bi-trophy fs-1 text-info mb-2"></i>
                                        <h6 className="card-title">التحديات</h6>
                                        <p className="card-text small text-muted">إدارة تحدياتك ومشاركاتك</p>
                                        <Link href="/sections/challenges" className="btn btn-outline-info btn-sm">
                                            <i className="bi bi-eye me-1"></i>
                                            عرض التحديات
                                        </Link>
                                        <Link href="/sections/challenges?addNew=true" className="btn btn-info btn-sm ms-2">
                                            <i className="bi bi-plus-lg me-1"></i>
                                            إضافة تحدي
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <i className="bi bi-mic fs-1 text-danger mb-2"></i>
                                        <h6 className="card-title">البودكاست</h6>
                                        <p className="card-text small text-muted">إدارة بودكاستاتك</p>
                                        <Link href="/sections/podcast" className="btn btn-outline-danger btn-sm">
                                            <i className="bi bi-eye me-1"></i>
                                            عرض البودكاست
                                        </Link>
                                        <Link href="/sections/podcast?addNew=true" className="btn btn-danger btn-sm ms-2">
                                            <i className="bi bi-plus-lg me-1"></i>
                                            إضافة بودكاست
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <i className="bi bi-link-45deg fs-1 text-secondary mb-2"></i>
                                        <h6 className="card-title">الروابط المختصرة</h6>
                                        <p className="card-text small text-muted">إدارة روابطك المختصرة</p>
                                        <Link href="/lnk" className="btn btn-outline-secondary btn-sm">
                                            <i className="bi bi-eye me-1"></i>
                                            عرض الروابط
                                        </Link>
                                        <Link href="/lnk" className="btn btn-secondary btn-sm ms-2">
                                            <i className="bi bi-plus-lg me-1"></i>
                                            إنشاء رابط
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </fieldset> */}
                                    </>

                                )}


                                {activeSection === 'security' && (
                                    <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">
                                        <b className='d-block mb-2'>الأمان وكلمة المرور</b>

                                        {/* <div>
                              <b className='d-block mb-2'>خصوصية الحساب </b>
                              <small>في حال تعيين الحساب إلى " خاص " لن يتمكن الباحثون عن مصممين الوصول لحسابك </small>
                              <div className="form-check form-switch mt-3">
                                  <input className="form-check-input" name='private_profile' type="checkbox" id="flexSwitchCheckChecked" onChange={handleChange} checked={form.private_profile === true} />
                                  <label className="form-check-label" for="flexSwitchCheckChecked">حساب خاص</label>
                              </div></div>
                          <hr /> */}
                                        <div>
                                            <label className='form-label'>تغيير كلمة المرور</label>
                                            <input name="password" type="password" value={form.password}
                                                onChange={handleChange} autoComplete='off'
                                                className="form-control" />
                                        </div>
                                    </fieldset>
                                )}

                                {activeSection === 'privacy' && (
                                    <fieldset className="border rounded-3 col-12 col-md-8 my-4 p-4">
                                        <div>
                                            <b className='d-block mb-2'>خصوصية الحساب </b>
                                            <small>في حال تعيين الحساب إلى " خاص " لن يتمكن الباحثون عن مصممين الوصول لحسابك </small>
                                            <div className="form-check form-switch mt-3">
                                                <input className="form-check-input" name='private_profile' type="checkbox" id="flexSwitchCheckChecked" onChange={handleChange} checked={form.private_profile === true} />
                                                <label className="form-check-label" for="flexSwitchCheckChecked">حساب خاص</label>
                                            </div></div>
                                        {/*  <hr />
                         <div>
                              <label className='form-label'>تغيير كلمة المرور</label>
                              <input name="password" type="password" value={form.password}
                                  onChange={handleChange} autoComplete='off'
                                  className="form-control" />
                          </div> */}
                                    </fieldset>
                                )}



                                <button type="submit"
                                    disabled={saving}
                                    className="btn btn-primary w-fit">
                                    {saving ? 'جار الحفظ …' : 'حفظ التعديلات'}
                                </button>
                            </div>
                        </>
                    )}

                    {/* -- Account content */}
                </form>
            )}

            {/* Delete Account Section */}
            {activeSection === 'deleteAccount' && (
                <DeleteAccountCard
                    user={user}
                    onCancel={() => {
                        // Navigate back to account settings
                        const currentUrl = new URL(window.location.href);
                        currentUrl.searchParams.delete('section');
                        window.history.pushState({}, '', currentUrl.pathname + currentUrl.search);
                        setActiveSection('default');
                    }}
                    onDeleteAccount={async () => {
                        try {
                            await deleteUserAccount();
                            toast.success('تم حذف حسابك بنجاح');
                            // Clear all user data and redirect to home
                            sessionStorage?.removeItem('token');
                            localStorage?.removeItem('user');
                            localStorage?.removeItem('token');
                            window.location.href = '/';
                        } catch (error) {
                            console.error('Error deleting account:', error);
                            toast.error('فشل في حذف الحساب. يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.');
                        }
                    }}
                />
            )}

        </>
    );
}